import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import './Technologies.css';
import { getApiUrl } from '../utils/api';

const ClientReview = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const shouldAnimate = !isMobile;

  const [ref, inView] = useInView({
    triggerOnce: shouldAnimate,
    threshold: shouldAnimate ? 0.1 : 0.01
  });
  
  const reviewsScrollRef = useRef(null);
  const [activeReviewIndex, setActiveReviewIndex] = useState(0);
  const [statsRef, statsInView] = useInView({
    triggerOnce: true,
    threshold: 0.3
  });

  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchReviews = (pageNum) => {
    if (loading || !hasMore) return;
    setLoading(true);
    const url = getApiUrl(`/api/reviews?page=${pageNum}&limit=10`);
    fetch(url)
      .then(res => res.json())
      .then(data => {
        console.log("Fetched reviews data:", data);
        if (data && Array.isArray(data.data)) {
          if (data.data.length === 0) {
            setHasMore(false);
          } else {
            setReviews(prev => [...prev, ...data.data]);
            setPage(pageNum + 1);
            if (data.data.length < 10) {
              setHasMore(false);
            }
          }
        } else {
          setHasMore(false);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching reviews:", err);
        setLoading(false);
      });
  };

  const [sentinelRef, sentinelInView] = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (sentinelInView && hasMore && !loading) {
      fetchReviews(page);
    }
  }, [sentinelInView, hasMore, loading, page]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: shouldAnimate ? {
        staggerChildren: 0.05,
        delayChildren: 0
      } : { duration: 0 }
    }
  };

  const containerScrollVariants = (direction) => {
    if (reviewsScrollRef.current) {
      const scrollAmount = 400;
      reviewsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  

  const itemVariants = {
    hidden: { y: shouldAnimate ? 20 : 0, opacity: shouldAnimate ? 0 : 1 },
    visible: {
      y: 0,
      opacity: 1,
      transition: shouldAnimate ? {
        duration: 0.4,
        ease: "easeOut"
      } : { duration: 0 }
    }
  };

  const scrollReviews = (direction) => {
    if (reviewsScrollRef.current) {
      const container = reviewsScrollRef.current;
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // On mobile, scroll by full card width + gap
        const cardWidth = window.innerWidth - 80; // Full width minus margins
        const gap = 20; // Mobile gap
        const scrollAmount = cardWidth + gap;
        
        container.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        });
      } else {
        // On desktop, use original scroll amount
        const scrollAmount = 400;
        container.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const container = reviewsScrollRef.current;
      if (container) {
        const cards = container.querySelectorAll('.review-card');
        const containerWidth = container.clientWidth;
        const isMobile = window.innerWidth <= 768;
        
        // Find the most visible card for pagination dots
        let mostVisibleIndex = 0;
        let maxVisibleArea = 0;
        
        cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const cardLeft = cardRect.left - containerRect.left;
          const cardRight = cardRect.right - containerRect.left;
          
          // Calculate visible area
          const visibleLeft = Math.max(0, cardLeft);
          const visibleRight = Math.min(containerWidth, cardRight);
          const visibleArea = Math.max(0, visibleRight - visibleLeft);
          
          if (visibleArea > maxVisibleArea) {
            maxVisibleArea = visibleArea;
            mostVisibleIndex = index;
          }
          
          // Remove all fade classes
          card.classList.remove('fade-left', 'fade-right', 'center');
          
          if (isMobile) {
            // On mobile, only show center class for fully visible cards
            if (cardLeft >= 0 && cardRight <= containerWidth) {
              card.classList.add('center');
            }
          } else {
            // Desktop behavior - check if card is partially off-screen on the left
            if (cardLeft < 0 && cardRight > 0) {
              card.classList.add('fade-left');
            }
            // Check if card is partially off-screen on the right
            else if (cardLeft < containerWidth && cardRight > containerWidth) {
              card.classList.add('fade-right');
            }
            // Card is fully visible
            else if (cardLeft >= 0 && cardRight <= containerWidth) {
              card.classList.add('center');
            }
          }
        });
        
        // Update active review index
        setActiveReviewIndex(mostVisibleIndex);
      }
    };

    const handleResize = () => {
      // Recalculate on resize
      setTimeout(handleScroll, 100);
    };

    const container = reviewsScrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);
      // Initial call to set correct states
      handleScroll();
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return (
    <section id="reviews" className="client-reviews-section" ref={ref}>
      <motion.div 
        className="client-reviews-container"
        variants={containerVariants}
        initial={shouldAnimate ? "hidden" : "visible"}
        animate={shouldAnimate ? (inView ? "visible" : "hidden") : "visible"}
      >
        <motion.div className="client-reviews" variants={itemVariants}>
          <div className="reviews-header">
            <div className="reviews-title-container">
              <motion.h3 className="reviews-title" variants={itemVariants}>
                What Our Clients Say
              </motion.h3>
              <motion.div className="reviews-navigation reviews-navigation-desktop" variants={itemVariants}>
                <button 
                  className="review-scroll-btn review-scroll-left" 
                  onClick={() => scrollReviews('left')}
                  aria-label="Scroll left"
                >
                  <FaChevronLeft />
                </button>
                <button 
                  className="review-scroll-btn review-scroll-right" 
                  onClick={() => scrollReviews('right')}
                  aria-label="Scroll right"
                >
                  <FaChevronRight />
                </button>
              </motion.div>
            </div>
          </div>
          <div className="reviews-scroll-container">
            <motion.div className="reviews-grid" ref={reviewsScrollRef}>
              {reviews.map((client, index) => (
                <motion.div
                  key={index}
                  className="review-card"
                  variants={itemVariants}
                  initial={shouldAnimate ? "hidden" : "visible"}
                  animate="visible"
                  whileHover={{ y: -8 }}
                  style={{ '--delay': `${index * 0.05}s` }}
                >
                  <div className="review-glow"></div>
                  <div className="review-content">
                    <div className="quote-icon">"</div>
                    <p className="review-text">{client.reviewMsg}</p>
                    <div className="review-divider"></div>
                    <div className="review-footer">
                      <div className="client-avatar">
                        <div className="avatar-ring"></div>
                        <img 
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(client.authorName)}&background=667eea&color=fff&size=128&bold=true`} 
                          alt={client.authorName}
                          className="avatar-img"
                        />
                      </div>
                      <div className="client-details">
                        <h4 className="client-name">{client.authorName}</h4>
                        <div className="service-tag">{client.authorRole}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              <div ref={sentinelRef} style={{ width: '10px', height: '1px', flexShrink: 0 }} />
            </motion.div>
          </div>
          {/* Pagination Dots - Mobile Only */}
          {isMobile && (
            <motion.div className="reviews-pagination-dots" variants={itemVariants}>
              {reviews.map((_, index) => (
                <button
                  key={index}
                  className={`pagination-dot ${index === activeReviewIndex ? 'active' : ''}`}
                  onClick={() => {
                    if (reviewsScrollRef.current) {
                      const container = reviewsScrollRef.current;
                      const cards = container.querySelectorAll('.review-card');
                      
                      if (cards[index]) {
                        const cardWidth = window.innerWidth - 80;
                        const gap = 20;
                        const scrollPosition = index * (cardWidth + gap);
                        
                        container.scrollTo({
                          left: scrollPosition,
                          behavior: 'smooth'
                        });
                      }
                    }
                  }}
                  aria-label={`Go to review ${index + 1}`}
                />
              ))}
            </motion.div>
          )}
          {/* <motion.div className="reviews-navigation reviews-navigation-mobile" variants={itemVariants}>
            <button 
              className="review-scroll-btn review-scroll-left" 
              onClick={() => scrollReviews('left')}
              aria-label="Scroll left"
            >
              <FaChevronLeft />
            </button>
            <button 
              className="review-scroll-btn review-scroll-right" 
              onClick={() => scrollReviews('right')}
              aria-label="Scroll right"
            >
              <FaChevronRight />
            </button>
          </motion.div> */}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ClientReview
